<script setup lang="ts">
/**
 * KEBIJAKAN PENGEMBALIAN DANA — tema v3.
 *
 * Janji di halaman ini sengaja SEMPIT dan bisa ditepati:
 * kegagalan di sisi kami = uang kembali; hasil ditolak verifikator = tidak,
 * karena penerimaan berkas di luar kendali kami (lihat /syarat bagian 2).
 * Menjanjikan lebih dari ini akan menjadi janji yang dilanggar sendiri.
 *
 * Seluruh kalimat dibawa PERSIS dari app/pages/refund.vue. Canonical menunjuk
 * alamat produksi '/refund' selama v3 masih prototipe.
 */

const situs = useSitusUrl()
const identitas = useIdentitas()

pakaiSeo({
  judul: 'Kebijakan Pengembalian Dana — tools.coreasia.id',
  deskripsi:
    'Kapan uangmu kembali: kode aktivasi gagal terbit, ebook tidak pernah terkirim, pembayaran ganda, atau layanan gagal total. Cara mengajukan dan lama prosesnya.',
  path: '/refund',
  jsonLd: [
    jsonLdRemah(situs, [
      ['Beranda', '/'],
      ['Kebijakan Pengembalian Dana', '/refund']
    ])
  ]
})
</script>

<template>
  <CaHalamanLegal
    judul="Kebijakan Pengembalian Dana"
    :terakhir="TANGGAL_KEBIJAKAN"
    ikon="i-lucide-receipt"
  >
    <template #pembuka>
      Kami menulis kebijakan ini apa adanya: ada hal yang pasti kami kembalikan, ada hal yang tidak.
      Lebih baik kamu tahu batasnya sebelum membayar Rp15.000 daripada kecewa setelahnya.
    </template>

    <!-- Dua kartu berikut memakai border tepi berwarna, bukan latar berwarna:
         di layar HP murah di bawah matahari, latar hijau/merah muda pucat
         hilang sama sekali, sementara garis 3px tetap terlihat. -->
    <CaCard class="border-s-4 border-s-[var(--ca-success)]">
      <div class="flex items-start gap-3">
        <UIcon name="i-lucide-circle-check" class="size-6 shrink-0 text-ok mt-0.5" aria-hidden="true" />
        <h2 class="ca-h3 text-ink">Uang pasti dikembalikan penuh bila…</h2>
      </div>
      <CaProsa class="mt-3">
        <ul>
          <li>
            <strong>Kode aktivasi gagal terbit setelah pembayaranmu diterima.</strong> Ini kegagalan
            di sisi kami. Kamu boleh memilih: kode diterbitkan ulang, atau uangmu dikembalikan
            penuh.
          </li>
          <li>
            <strong>Kamu terlanjur membayar dua kali untuk pesanan yang sama.</strong> Pembayaran
            kelebihan dikembalikan penuh.
          </li>
          <li>
            <strong>Kode aktivasi tidak bisa ditukarkan karena kesalahan sistem kami</strong>, dan
            kami tidak berhasil memperbaikinya dalam waktu wajar.
          </li>
          <li>
            <strong>Layanan gagal total memproses berkasmu karena gangguan di sisi kami</strong>,
            dan sampai masa aktif kodemu berakhir kamu tidak pernah menerima hasil apa pun.
          </li>
        </ul>
      </CaProsa>
    </CaCard>

    <!-- Ebook sebelumnya tidak disebut sama sekali, padahal halaman buku
         merujuk ke sini. Prinsipnya sama dengan kode aktivasi: kegagalan di
         sisi kami = uang kembali. Yang berbeda hanya satu hal: berkas digital
         yang sudah terunduh tidak bisa "dikembalikan", jadi berubah pikiran
         setelah mengunduh tidak termasuk — tetapi kasusnya tetap boleh diajukan. -->
    <CaCard id="ebook" class="border-s-4 border-s-[var(--ca-success)] scroll-mt-20">
      <div class="flex items-start gap-3">
        <UIcon name="i-lucide-book-open" class="size-6 shrink-0 text-ok mt-0.5" aria-hidden="true" />
        <h2 class="ca-h3 text-ink">Untuk ebook</h2>
      </div>
      <CaProsa class="mt-3">
        <ul>
          <li>
            <strong>Uang dikembalikan penuh bila pembayaranmu diterima tetapi bukunya tidak pernah
            bisa kami kirimkan</strong> — berkasnya gagal disiapkan, atau tautan unduhnya tidak
            pernah sampai — dan kami tidak berhasil memperbaikinya dalam waktu wajar. Kamu boleh
            memilih: tautan dikirim ulang, atau uangmu dikembalikan.
          </li>
          <li>
            <strong>Pembayaran ganda</strong> untuk buku yang sama dikembalikan penuh.
          </li>
          <li>
            <strong>Berubah pikiran setelah berkas terunduh tidak termasuk</strong>, karena berkas
            digital yang sudah tersimpan tidak bisa ditarik kembali. Kalau isinya tidak sesuai
            dengan yang dijanjikan di halaman buku, tetap hubungi kami — kasus seperti ini kami
            tinjau satu per satu.
          </li>
        </ul>
      </CaProsa>
    </CaCard>

    <CaCard class="border-s-4 border-s-[var(--ca-danger)]">
      <div class="flex items-start gap-3">
        <UIcon name="i-lucide-circle-x" class="size-6 shrink-0 text-bad mt-0.5" aria-hidden="true" />
        <h2 class="ca-h3 text-ink">Uang tidak dikembalikan bila…</h2>
      </div>
      <CaProsa class="mt-3">
        <ul>
          <li>
            <strong>Berkasmu sudah diproses dan hasilnya kamu terima, tetapi verifikator instansi
            menolaknya.</strong>
            Kami harus jujur soal ini: penerimaan berkas ditentukan oleh petugas dan sistem instansi
            tujuan, di luar kendali kami — bisa karena syarat isi dokumen, ketentuan yang berubah di
            tengah siklus, atau penilaian verifikator. Yang kami jual adalah pekerjaan penyesuaian
            berkas beserta laporannya, bukan hasil penerimaan. Ini juga alasan kami tidak pernah
            memakai kalimat "dijamin lolos" di mana pun.
          </li>
          <li>
            <strong>Sebuah berkas dilaporkan "target tidak tercapai".</strong> Laporan semacam itu
            adalah hasil kerja yang sah, bukan kegagalan layanan: kami memilih memberitahumu dengan
            terus terang daripada diam-diam membuat dokumenmu buram sampai tidak terbaca.
          </li>
          <li>
            <strong>Kamu berubah pikiran</strong> setelah kode ditukarkan dan layanan mulai dipakai.
          </li>
          <li>
            <strong>Masa aktif 3 hari habis sementara kamu tidak sempat memakainya.</strong> Masa
            aktif baru mulai berjalan saat kode pertama kali ditukarkan, jadi kamu punya kendali
            penuh atas kapan waktunya mulai.
          </li>
          <li>
            <strong>Berkas sumbermu tidak dapat dibaca sistem</strong> (misalnya berkas rusak, atau
            format HEIC/HEIF dari iPhone yang belum kami dukung) padahal batasannya sudah kami
            sebutkan di halaman produk. Bila kamu belum sempat memakai kodemu sama sekali, hubungi
            kami — kasus seperti ini kami tinjau satu per satu.
          </li>
        </ul>
      </CaProsa>

      <CaAlert tone="info" title="Ragu kasusmu masuk yang mana?" class="mt-4">
        Ajukan saja. Kami lebih suka meninjau kasus yang meragukan daripada menolaknya lewat aturan
        tertulis.
      </CaAlert>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">Cara mengajukan</h2>
      <CaProsa class="mt-3">
        <ol>
          <li>
            Siapkan <strong>bukti pembayaran</strong> (tangkapan layar mutasi atau struk dari
            aplikasi pembayaranmu, yang memperlihatkan tanggal dan nominal).
          </li>
          <li>
            Sebutkan <strong>nomor pesanan</strong>, <strong>kode aktivasi</strong> atau
            <strong>kode lisensi ebook</strong> bila sempat terbit, dan <strong>alamat email</strong>
            yang kamu isi saat membeli bila ada.
          </li>
          <li>Jelaskan singkat apa yang terjadi — satu atau dua kalimat sudah cukup.</li>
          <li>
            Kirimkan lewat kanal resmi di bawah. <strong>Nomor rekening atau nomor e-wallet tujuan
            jangan dikirim lebih dulu</strong>; kami akan memintanya setelah pengajuanmu
            diverifikasi.
          </li>
        </ol>
        <p v-if="identitas.adaKontak">Kanal resmi pengajuan:</p>
      </CaProsa>

      <CaKontak
        class="mt-4"
        konteks="Pengembalian Dana"
        pesan="Halo, saya ingin mengajukan pengembalian dana. Bukti bayar saya lampirkan."
        subjek="Pengajuan pengembalian dana — tools.coreasia.id"
      />
      <CaAlert v-if="!identitas.adaKontak" tone="warn" title="Kanal pengajuan belum tersedia" class="mt-4">
        Kanal resmi pengajuan belum dicantumkan pada halaman ini. Jangan melakukan pembayaran
        sebelum bagian ini terisi.
      </CaAlert>

      <CaAlert tone="bad" title="Waspada penipuan" class="mt-4">
        Kami tidak pernah meminta PIN, OTP, kata sandi, atau nomor kartumu — untuk alasan apa pun.
        Siapa pun yang meminta hal itu mengatasnamakan kami adalah penipu.
      </CaAlert>
    </CaCard>

    <CaCard>
      <h2 class="ca-h3 text-ink">Berapa lama diproses</h2>
      <CaProsa class="mt-3">
        <ul>
          <li>
            <strong>Pengajuan ditinjau paling lambat 2 hari kerja</strong> sejak diterima lengkap.
            Kami akan memberi kabar hasil peninjauannya lewat kanal yang sama.
          </li>
          <li>
            <strong>Dana dikirim paling lambat 7 hari kerja</strong> setelah pengajuan disetujui dan
            rekening tujuan kamu berikan.
          </li>
          <li>
            Waktu dana benar-benar masuk ke rekeningmu masih bergantung pada bank atau penyedia
            e-wallet-mu, biasanya 1 hari kerja setelah kami kirim.
          </li>
          <li>
            Pengembalian dikirim <strong>penuh sesuai nominal yang kamu bayar</strong>. Biaya
            transfer, bila ada, kami yang tanggung.
          </li>
        </ul>
        <p>
          Ajukan paling lambat <strong>30 hari</strong> sejak tanggal pembayaran, selagi catatan
          pesanannya masih mudah kami telusuri.
        </p>
      </CaProsa>
    </CaCard>

    <template #tautan>
      <NuxtLink to="/syarat" class="text-brand font-semibold underline underline-offset-2">
        Syarat Layanan
      </NuxtLink>
      dan
      <NuxtLink to="/privasi" class="text-brand font-semibold underline underline-offset-2">
        Kebijakan Privasi
      </NuxtLink>.
    </template>
  </CaHalamanLegal>
</template>
